/**
 * Application Routes Configuration
 * Centralized route definitions with protected route wrapper
 */

import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import AppLayout from './components/AppLayout';

// Lazy load components for better performance
const LandingPage = lazy(() => import('./components/LandingPage'));
const LoginPage = lazy(() => import('./components/LoginPage'));
const PricingPage = lazy(() => import('./components/PricingPage'));
const SettingsPage = lazy(() => import('./components/SettingsPage'));
const AuthCallback = lazy(() => import('./components/AuthCallback'));
const BillingSuccess = lazy(() => import('./components/BillingSuccess'));
const CloudOAuthCallback = lazy(() => import('./components/CloudOAuthCallback'));
const PrivacyPolicyPage = lazy(() => import('./components/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./components/TermsOfServicePage'));

// Redirect /news to jur.news (migrated)
const RedirectToJurNews: React.FC = () => {
    React.useEffect(() => { window.location.href = 'https://jur.news'; }, []);
    return null;
};


// Loading spinner for lazy-loaded components
const LoadingSpinner: React.FC = () => (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-500">Loading...</p>
        </div>
    </div>
);

// Protected Route wrapper - redirects to login if not authenticated
interface ProtectedRouteProps {
    children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
    const { isAuthenticated, isLoading } = useAuth();
    const location = useLocation();

    // Show loading while checking auth state
    if (isLoading) {
        return <LoadingSpinner />;
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        // Save the attempted URL to redirect back after login
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return <>{children}</>;
};

// Public Route wrapper - redirects to app if already authenticated
interface PublicRouteProps {
    children: React.ReactNode;
    redirectIfAuth?: boolean;
}

export const PublicRoute: React.FC<PublicRouteProps> = ({
    children,
    redirectIfAuth = false
}) => {
    const { isAuthenticated, isLoading } = useAuth();

    // Show loading while checking auth state
    if (isLoading) {
        return <LoadingSpinner />;
    }

    // Redirect to app if authenticated and redirectIfAuth is true
    if (isAuthenticated && redirectIfAuth) {
        return <Navigate to="/app" replace />;
    }

    return <>{children}</>;
};

// Route configuration
interface AppRoutesProps {
    currentLanguage: string;
    onLanguageChange: (lang: string) => void;
}

export const AppRoutes: React.FC<AppRoutesProps> = ({
    currentLanguage,
    onLanguageChange
}) => {
    return (
        <Suspense fallback={<LoadingSpinner />}>
            <Routes>
                {/* Public Routes */}
                <Route
                    path="/"
                    element={
                        <PublicRoute redirectIfAuth>
                            <LandingPage
                                currentLanguage={currentLanguage}
                                onLanguageChange={onLanguageChange}
                            />
                        </PublicRoute>
                    }
                />

                <Route
                    path="/login"
                    element={
                        <PublicRoute redirectIfAuth>
                            <LoginPage currentLanguage={currentLanguage} />
                        </PublicRoute>
                    }
                />

                <Route
                    path="/pricing"
                    element={
                        <PricingPage currentLanguage={currentLanguage} />
                    }
                />

                <Route
                    path="/privacy"
                    element={
                        <PrivacyPolicyPage currentLanguage={currentLanguage} onClose={() => window.history.back()} />
                    }
                />

                <Route
                    path="/terms"
                    element={
                        <TermsOfServicePage currentLanguage={currentLanguage} onClose={() => window.history.back()} />
                    }
                />

                {/* News - Migrated to jur.news */}
                <Route path="/news/*" element={<RedirectToJurNews />} />
                {/* OAuth & Auth Callbacks */}
                <Route path="/auth/callback/:provider" element={<AuthCallback />} />
                <Route path="/auth/verify" element={<AuthCallback />} />

                {/* Billing Success - Redirect to app with success message */}
                <Route
                    path="/billing/success"
                    element={
                        <ProtectedRoute>
                            <BillingSuccess />
                        </ProtectedRoute>
                    }
                />

                {/* Cloud OAuth Callback (弹窗页面，不需要完整 AppLayout) */}
                <Route path="/app/cloud-callback" element={<CloudOAuthCallback />} />

                {/* Protected Routes - All under /app */}
                <Route
                    path="/app/*"
                    element={
                        <ProtectedRoute>
                            <AppLayout
                                currentLanguage={currentLanguage}
                                onLanguageChange={onLanguageChange}
                            />
                        </ProtectedRoute>
                    }
                />

                {/* Fallback - redirect unknown routes */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Suspense>
    );
};

export default AppRoutes;
